import { setSnackbar } from 'flow/actions/app-actions';
import { safeStringify } from './sortUtils';
import i18next from 'i18next';
import { store } from 'flow/configure-store';

/**
 * Helper function to safely copy values to clipboard
 * @param value The value to copy
 * @param key Optional key/label to include with the value
 * @param path Optional full path to include with the value
 */
export const copyToClipboard = (
    value: any,
    key: string | null = null,
    path: string | null = null,
): void => {
    try {
        let textToCopy: string;

        // Format based on the parameters provided
        if (path) {
            // If full path is provided, format as path with value
            textToCopy = `Path: ${path}\nValue: ${safeStringify(value, 3)}`;
        } else if (key) {
            // If key is provided, format as key-value pair
            const keyStr = isNaN(Number(key)) ? key : `[${key}]`;
            textToCopy = `${keyStr}: ${safeStringify(value, 3)}`;
        } else {
            // Just the value itself
            textToCopy = safeStringify(value, 3);
        }

        // Use the modern clipboard API
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                store.dispatch(
                    setSnackbar({
                        message: i18next.t('snackbar.copied_to_clipboard'),
                    }),
                );
            })
            .catch((err) => {
                console.error('Failed to copy: ', err);
                // Fallback for older browsers
                fallbackCopyToClipboard(textToCopy);
            });
    } catch (error) {
        console.error('Error copying to clipboard:', error);
    }
};

/**
 * Fallback method for copying to clipboard in browsers that don't support the Clipboard API
 * @param text The text to copy
 */
const fallbackCopyToClipboard = (text: string): void => {
    try {
        // Create a temporary text area element
        const textArea = document.createElement('textarea');
        textArea.value = text;

        // Make it invisible but part of the document
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);

        // Select and copy the text
        textArea.select();
        document.execCommand('copy');

        // Clean up
        document.body.removeChild(textArea);

        // Show snackbar notification instead of console log
        store.dispatch(
            setSnackbar({
                message: i18next.t('snackbar.copied_to_clipboard'),
            }),
        );
    } catch (err) {
        console.error('Fallback clipboard method failed:', err);
    }
};
